/**
 * Product Bundle Options Handler
 *
 * Admin setup (no code changes):
 * 1. Create a collection with bundle products
 * 2. Tag each product: bundle-collection:your-collection-handle
 *    (e.g. bundle-collection:lava-studio)
 *
 * The current product is excluded from its own bundle options (Liquid + JS).
 * Legacy PX guitar / Valeton / EFNOTE rules still work via Liquid data attributes.
 */
class ProductBundleOptions {
  constructor(container) {
    this.bundleContainer =
      container ||
      document.querySelector('.product-bundle-options');

    if (!this.bundleContainer) return;

    this.sectionId = this.bundleContainer.dataset.sectionId || '';
    this.currentProductId = this.bundleContainer.dataset.currentProductId || '';
    this.bundleCollectionHandle =
      this.bundleContainer.dataset.bundleCollectionHandle || '';
    this.bundleSource = this.bundleContainer.dataset.bundleSource || '';
    this.isTagBasedBundle = this.bundleSource === 'tag';
    this.isLegacyBundle = !this.isTagBasedBundle;

    this.checkboxes = this.getBundleCheckboxes();
    this.selectedList = this.bundleContainer.querySelector(
      '.product-bundle-options__selected-list'
    );
    this.totalPrice = this.bundleContainer.querySelector(
      '.product-bundle-options__total-price'
    );
    this.originalPrice = null;
    this.specialOfferSection = this.bundleContainer.querySelector(
      '.product-bundle-special-offer'
    );
    this.freeAccessoryRadios = this.bundleContainer.querySelectorAll(
      '.special-offer-option__radio'
    );
    this.bundleDiscount =
      parseInt(this.bundleContainer.dataset.bundleDiscount, 10) || 0;
    this.hasSpecialOffer =
      this.bundleContainer.dataset.specialOffer === 'true' &&
      this.specialOfferSection;
    this.isValetonBag =
      this.bundleContainer.dataset.valetonBag === 'true';
    this.valetonBagOptions = this.bundleContainer.querySelectorAll(
      '.product-bundle-option--valeton-bag'
    );
    this.variantChangeUnsubscriber = undefined;

    this.init();
  }

  getBundleCheckboxes() {
    const all = this.bundleContainer.querySelectorAll(
      '.product-bundle-option__checkbox'
    );
    if (!this.currentProductId) return Array.from(all);
    return Array.from(all).filter((checkbox) => !this.isCurrentProduct(checkbox));
  }

  isCurrentProduct(checkbox) {
    if (!this.currentProductId) return false;
    const optionProductId =
      checkbox.dataset.productId ||
      checkbox.closest('.product-bundle-option')?.dataset.productId;
    return String(optionProductId) === String(this.currentProductId);
  }

  init() {
    if (this.totalPrice) {
      this.originalPrice = this.totalPrice.textContent;
    }

    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        this.updateSummary();
        this.checkSpecialOffer();
      });
    });

    this.freeAccessoryRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        this.updateSummary();
      });
    });

    if (
      this.isValetonBag &&
      typeof subscribe !== 'undefined' &&
      typeof PUB_SUB_EVENTS !== 'undefined'
    ) {
      this.updateValetonBagVisibility();
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        () => {
          this.updateValetonBagVisibility();
          this.updateSummary();
        }
      );
      const variantInput = document.querySelector(
        '.product-form input[name="id"]'
      );
      if (variantInput) {
        variantInput.addEventListener('change', () => {
          this.updateValetonBagVisibility();
          this.updateSummary();
        });
      }
    }

    this.updateSummary();
    this.checkSpecialOffer();
  }

  updateValetonBagVisibility() {
    if (!this.isValetonBag || this.valetonBagOptions.length === 0) return;
    const variantInput = document.querySelector(
      '.product-form input[name="id"]'
    );
    const currentVariantId = variantInput ? String(variantInput.value) : '';
    this.valetonBagOptions.forEach((option) => {
      const mainVariantIds = option.dataset.mainVariantIds || '';
      const ids = mainVariantIds
        ? mainVariantIds.split(',').map((id) => id.trim())
        : [];
      const show = ids.length === 0 || ids.includes(currentVariantId);
      option.style.display = show ? '' : 'none';
      const checkbox = option.querySelector('.product-bundle-option__checkbox');
      if (checkbox && !show) checkbox.checked = false;
    });
  }

  updateSummary() {
    const selectedItems = [];
    let totalPrice = this.getMainProductPrice();

    this.checkboxes.forEach((checkbox) => {
      if (this.isCurrentProduct(checkbox)) return;
      if (!checkbox.checked) return;

      const productTitle = checkbox.dataset.productTitle;
      const productPrice = this.parsePrice(checkbox.dataset.productPrice);
      const optionEl = checkbox.closest('.product-bundle-option');
      const isValetonBag =
        optionEl &&
        optionEl.classList.contains('product-bundle-option--valeton-bag');

      selectedItems.push(
        isValetonBag ? productTitle + ' (Discounted)' : productTitle
      );
      totalPrice += productPrice;
    });

    this.freeAccessoryRadios.forEach((radio) => {
      if (!radio.checked) return;
      const productTitle = radio.dataset.productTitle;
      const productPrice = this.parsePrice(radio.dataset.productPrice);
      selectedItems.push(productTitle + ' (FREE)');
      totalPrice += productPrice;
    });

    if (this.hasSpecialOffer && this.bundleDiscount > 0) {
      const totalCheckboxes = this.checkboxes.length;
      const checkedCheckboxes = this.checkboxes.filter((cb) => cb.checked)
        .length;
      const hasFreeAccessory = Array.from(this.freeAccessoryRadios).some(
        (radio) => radio.checked
      );

      if (totalCheckboxes > 0 && checkedCheckboxes === totalCheckboxes) {
        totalPrice -= hasFreeAccessory
          ? 50 + this.bundleDiscount
          : this.bundleDiscount;
      }
    }

    if (this.selectedList) {
      this.selectedList.textContent =
        selectedItems.length > 0 ? selectedItems.join(', ') : 'None selected';
    }

    if (this.totalPrice) {
      this.totalPrice.textContent = this.formatPrice(totalPrice);
    }
  }

  getMainProductPrice() {
    const priceElement =
      document.querySelector('.price__regular .price-item--regular') ||
      document.querySelector('.price .price-item--regular') ||
      document.querySelector('[data-price]') ||
      document.querySelector('.price');

    if (priceElement) {
      return this.parsePrice(priceElement.textContent);
    }

    if (this.originalPrice) {
      return this.parsePrice(this.originalPrice);
    }

    return 0;
  }

  parsePrice(priceString) {
    let cleanPrice = priceString.replace(/[^\d.,]/g, '');

    if (cleanPrice.includes(',') && cleanPrice.includes('.')) {
      cleanPrice = cleanPrice.replace(/,/g, '');
    } else if (cleanPrice.includes(',') && !cleanPrice.includes('.')) {
      const parts = cleanPrice.split(',');
      if (parts.length === 2 && parts[1].length === 2) {
        cleanPrice = cleanPrice.replace(',', '.');
      } else {
        cleanPrice = cleanPrice.replace(/,/g, '');
      }
    }

    const price = parseFloat(cleanPrice);
    return isNaN(price) ? 0 : price;
  }

  formatPrice(price) {
    const shopCurrency =
      window.Shopify?.currency?.active ||
      this.bundleContainer.dataset.shopCurrency ||
      'SGD';

    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: shopCurrency,
    }).format(price);
  }

  checkSpecialOffer() {
    if (!this.hasSpecialOffer) return;

    const totalCheckboxes = this.checkboxes.length;
    const checkedCheckboxes = this.checkboxes.filter((cb) => cb.checked).length;

    if (totalCheckboxes > 0 && checkedCheckboxes === totalCheckboxes) {
      this.specialOfferSection.style.display = 'block';
    } else {
      this.specialOfferSection.style.display = 'none';
      this.freeAccessoryRadios.forEach((radio) => {
        radio.checked = false;
      });
    }
  }

  getSelectedProducts() {
    const selectedProducts = [];

    this.checkboxes.forEach((checkbox) => {
      if (this.isCurrentProduct(checkbox) || !checkbox.checked) return;

      const optionEl = checkbox.closest('.product-bundle-option');
      const isValetonBag =
        optionEl &&
        optionEl.classList.contains('product-bundle-option--valeton-bag');
      const item = {
        id: checkbox.value,
        title: checkbox.dataset.productTitle,
        price: checkbox.dataset.productPrice,
      };

      if (isValetonBag) {
        item.properties = { _valeton_bag_discount: 'true' };
      }

      selectedProducts.push(item);
    });

    this.freeAccessoryRadios.forEach((radio) => {
      if (!radio.checked) return;
      selectedProducts.push({
        id: radio.value,
        title: radio.dataset.productTitle,
        price: 'FREE',
        isFree: true,
      });
    });

    return selectedProducts;
  }

  getBundleOfferKey() {
    return (
      this.bundleCollectionHandle ||
      this.bundleContainer?.dataset.bundleCollectionHandle ||
      'bundle'
    );
  }
}

/**
 * Enhanced Product Form for Bundle Support
 */
class BundleProductForm extends HTMLElement {
  constructor() {
    super();
    const sectionId = this.dataset.sectionId || '';
    const bundleContainer = sectionId
      ? document.querySelector(
          `.product-bundle-options[data-section-id="${sectionId}"]`
        )
      : document.querySelector('.product-bundle-options');

    this.bundleOptions = bundleContainer
      ? new ProductBundleOptions(bundleContainer)
      : null;
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.cart =
      document.querySelector('cart-notification') ||
      document.querySelector('cart-drawer');

    if (this.form && this.submitButton) {
      this.init();
    }
  }

  init() {
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

    this.setLoadingState(true);

    try {
      const mainProductId = this.form.querySelector('input[name="id"]').value;
      const quantity =
        this.form.querySelector('input[name="quantity"]')?.value || 1;

      const itemsToAdd = [
        {
          id: mainProductId,
          quantity: parseInt(quantity, 10),
        },
      ];

      const bundleOfferKey =
        this.bundleOptions?.getBundleOfferKey?.() || 'bundle';
      const selectedProducts =
        this.bundleOptions?.getSelectedProducts?.() || [];

      selectedProducts.forEach((product) => {
        if (product.isFree) {
          itemsToAdd.push({
            id: product.id,
            quantity: 1,
            properties: {
              _free_item: 'true',
              _bundle_offer: bundleOfferKey,
            },
          });
        } else {
          itemsToAdd.push({
            id: product.id,
            quantity: 1,
            ...(product.properties && Object.keys(product.properties).length
              ? { properties: product.properties }
              : {}),
          });
        }
      });

      const response = await this.addMultipleItemsToCart(itemsToAdd);

      if (response.status) {
        this.handleError(response.description || response.message);
        return;
      }

      if (this.cart && typeof this.cart.renderContents === 'function') {
        try {
          this.updateCart(response);
        } catch (error) {
          console.warn('Cart update failed, redirecting to cart page:', error);
          window.location.href = '/cart';
        }
      } else {
        window.location.href = '/cart';
      }
    } catch (error) {
      console.error('Error adding items to cart:', error);
      this.handleError(
        'There was an error adding items to your cart. Please try again.'
      );
    } finally {
      this.setLoadingState(false);
    }
  }

  async addMultipleItemsToCart(items) {
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items }),
    };

    if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
      try {
        const sections = this.cart
          .getSectionsToRender()
          .map((section) => section.id);
        config.body = JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        });
      } catch (error) {
        console.warn('Error getting sections for cart update:', error);
      }
    }

    const response = await fetch('/cart/add.js', config);
    return response.json();
  }

  updateCart(cartData) {
    try {
      if (!this.cart || typeof this.cart.renderContents !== 'function') {
        window.location.href = '/cart';
        return;
      }

      let payload = cartData;
      if (
        cartData.items &&
        Array.isArray(cartData.items) &&
        cartData.items.length > 0
      ) {
        const lastItem = cartData.items[cartData.items.length - 1];
        payload = {
          key: lastItem.key,
          sections: cartData.sections || {},
        };
        const sectionIds = this.cart.getSectionsToRender().map((s) => s.id);
        const hasSections = sectionIds.every((id) => payload.sections[id]);
        if (!hasSections) {
          window.location.href = '/cart';
          return;
        }
      }

      this.cart.renderContents(payload);
    } catch (error) {
      window.location.href = '/cart';
    }
  }

  setLoadingState(loading) {
    if (loading) {
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');
      const spinner = this.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');

      if (this.bundleOptions?.bundleContainer) {
        this.bundleOptions.bundleContainer.classList.add('loading');
      }
    } else {
      this.submitButton.removeAttribute('aria-disabled');
      this.submitButton.classList.remove('loading');
      const spinner = this.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');

      if (this.bundleOptions?.bundleContainer) {
        this.bundleOptions.bundleContainer.classList.remove('loading');
      }
    }
  }

  handleError(message) {
    console.error('Cart error:', message);

    const errorWrapper = this.querySelector(
      '.product-form__error-message-wrapper'
    );
    if (errorWrapper) {
      const errorMessage = errorWrapper.querySelector(
        '.product-form__error-message'
      );
      if (errorMessage) {
        errorMessage.textContent = message;
        errorWrapper.removeAttribute('hidden');
      }
    } else {
      alert(message);
    }
  }
}

document.addEventListener('DOMContentLoaded', function () {
  if (!customElements.get('bundle-product-form')) {
    customElements.define('bundle-product-form', BundleProductForm);
  }
});
